import { useState, useCallback } from 'react';
import axios from 'axios';
import { getAuthHeaders, DEFAULT_COACH_PIN } from './workoutHelpers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =============================================
// useCoachMode — trainer-facing structural editing during an active workout.
// Extracted verbatim from ActiveWorkoutInterface.js (Phase B decomposition):
// exercise picker + add/replace/remove/move + apply-to-template + coach axios.
// No behavior change — same endpoints, same optimistic updates, same errors.
// =============================================
export const useCoachMode = ({
  isCoachMode,
  setIsCoachMode,
  workout,
  workoutData,
  setWorkoutData,
  currentExerciseIndex,
  setCurrentExerciseIndex,
  allExercisesCatalog,
  setAllExercisesCatalog,
  clientExerciseUsage,
  setClientExerciseUsage,
  replaceExerciseIndex,
  setReplaceExerciseIndex,
  setExercisePickerMode,
  setShowExercisePicker,
}) => {
  const [coachModeError, setCoachModeError] = useState(null);

  // Fetch full exercise catalog when coach mode picker opens
  const openExercisePicker = useCallback(async (mode, replaceIdx = null) => {
    setExercisePickerMode(mode);
    setReplaceExerciseIndex(replaceIdx);
    setShowExercisePicker(true);

    // Fetch exercise catalog if not loaded yet
    if (allExercisesCatalog.length === 0) {
      try {
        const response = await axios.get(`${API}/exercises/list`, { headers: getAuthHeaders() });
        setAllExercisesCatalog(response.data || []);
      } catch (err) {
        console.error('[CoachMode] Error fetching exercises catalog:', err);
      }
    }

    // Fetch client exercise usage if not loaded yet (critical for usage badges)
    if (Object.keys(clientExerciseUsage).length === 0 && workout?.client_id && workout.client_id !== 'template') {
      try {
        const response = await axios.get(`${API}/exercises/client-usage?client_id=${workout.client_id}`, { headers: getAuthHeaders() });
        if (response.data) {
          setClientExerciseUsage(response.data);
          console.log('[CoachMode] Loaded client exercise usage:', Object.keys(response.data).length, 'exercises');
        }
      } catch (err) {
        console.error('[CoachMode] Error fetching client exercise usage:', err);
      }
    }
  }, [allExercisesCatalog.length, clientExerciseUsage, workout?.client_id, setExercisePickerMode, setReplaceExerciseIndex, setShowExercisePicker, setAllExercisesCatalog, setClientExerciseUsage]);

  // Stable callback for closing exercise picker
  const handleCloseExercisePicker = useCallback(() => {
    setShowExercisePicker(false);
    setReplaceExerciseIndex(null);
  }, [setShowExercisePicker, setReplaceExerciseIndex]);

  // Verify PIN for coach mode
  const verifyCoachPin = useCallback((pin) => {
    return pin === DEFAULT_COACH_PIN;
  }, []);

  // Activate coach mode
  const activateCoachMode = useCallback(() => {
    setIsCoachMode(true);
    console.log('[CoachMode] Activated for workout:', workout.id);
  }, [workout.id, setIsCoachMode]);

  // Coach Mode API helper - adds coach mode header AND auth header
  const coachModeAxios = useCallback(async (method, url, data = null) => {
    const config = {
      headers: {
        ...getAuthHeaders(),
        'X-Coach-Mode': 'true'
      }
    };

    if (method === 'delete') {
      return axios.delete(url, config);
    } else if (method === 'post') {
      return axios.post(url, data, config);
    } else if (method === 'put') {
      return axios.put(url, data, config);
    }
  }, []);

  // Coach Mode: Add Exercise (Planner-consistent initialization)
  const handleCoachAddExercise = useCallback(async (exercise) => {
    if (!isCoachMode) return;

    try {
      setCoachModeError(null);
      // Auto-load client's last weight for this exercise (matching Planner behavior)
      const usage = clientExerciseUsage[exercise.id];
      const targetWeight = usage?.last_weight || 0;

      await coachModeAxios('post', `${API}/workouts/${workout.id}/coach/add-exercise`, {
        exercise_id: exercise.id,
        position: null, // Append to end
        target_sets: 4,
        target_reps: 12,
        target_weight: targetWeight,
        rest_time: 120,
        is_superset: false,
        superset_group: null
      });

      // Refresh workout data
      const response = await axios.get(`${API}/workouts/${workout.id}`, { headers: getAuthHeaders() });
      if (response.data?.exercises) {
        // Update parent state through setWorkoutData
        const newExercises = response.data.exercises.map((ex, idx) => ({
          ...ex,
          actual_sets: workoutData[idx]?.actual_sets || []
        }));
        setWorkoutData(newExercises);
        // Also update the workout.exercises reference
        workout.exercises = response.data.exercises;
      }

      // Don't close picker - allow adding multiple exercises (same as Planner)
      console.log('[CoachMode] Exercise added:', exercise.name);
    } catch (err) {
      console.error('[CoachMode] Error adding exercise:', err);
      setCoachModeError('Error al añadir ejercicio');
    }
  }, [isCoachMode, workout.id, workoutData, setWorkoutData, coachModeAxios, clientExerciseUsage, workout]);

  // Coach Mode: Replace Exercise (preserves original sets/superset config)
  const handleCoachReplaceExercise = useCallback(async (exercise) => {
    if (!isCoachMode || replaceExerciseIndex === null) return;

    try {
      setCoachModeError(null);
      await coachModeAxios('post', `${API}/workouts/${workout.id}/coach/replace-exercise`, {
        exercise_index: replaceExerciseIndex,
        new_exercise_id: exercise.id,
        preserve_sets: true
      });

      // Refresh workout data
      const response = await axios.get(`${API}/workouts/${workout.id}`, { headers: getAuthHeaders() });
      if (response.data?.exercises) {
        const newExercises = response.data.exercises.map((ex, idx) => ({
          ...ex,
          actual_sets: workoutData[idx]?.actual_sets || []
        }));
        setWorkoutData(newExercises);
        workout.exercises = response.data.exercises;
      }

      setShowExercisePicker(false);
      setReplaceExerciseIndex(null);
      console.log('[CoachMode] Exercise replaced at index:', replaceExerciseIndex);
    } catch (err) {
      console.error('[CoachMode] Error replacing exercise:', err);
      setCoachModeError('Error al reemplazar ejercicio');
    }
  }, [isCoachMode, replaceExerciseIndex, workout.id, workoutData, setWorkoutData, coachModeAxios, workout, setShowExercisePicker, setReplaceExerciseIndex]);

  // Coach Mode: Remove Exercise
  const handleCoachRemoveExercise = useCallback(async (exerciseIndex) => {
    if (!isCoachMode) return;

    // Confirm removal
    if (!window.confirm(`¿Seguro que quieres eliminar este ejercicio?`)) {
      return;
    }

    try {
      setCoachModeError(null);
      await coachModeAxios('delete', `${API}/workouts/${workout.id}/coach/remove-exercise/${exerciseIndex}`);

      // Refresh workout data
      const response = await axios.get(`${API}/workouts/${workout.id}`, { headers: getAuthHeaders() });
      if (response.data?.exercises) {
        const newExercises = response.data.exercises.map((ex, idx) => ({
          ...ex,
          actual_sets: workoutData[idx]?.actual_sets || []
        }));
        setWorkoutData(newExercises);
        workout.exercises = response.data.exercises;

        // Adjust current index if needed
        if (currentExerciseIndex >= response.data.exercises.length) {
          setCurrentExerciseIndex(Math.max(0, response.data.exercises.length - 1));
        }
      }

      console.log('[CoachMode] Exercise removed at index:', exerciseIndex);
    } catch (err) {
      console.error('[CoachMode] Error removing exercise:', err);
      setCoachModeError(err.response?.data?.detail || 'Error al eliminar ejercicio');
    }
  }, [isCoachMode, workout.id, workoutData, setWorkoutData, currentExerciseIndex, setCurrentExerciseIndex, coachModeAxios, workout]);

  // PART 3 FIX: Move exercise up/down (touch-friendly reorder fallback)
  // BUGFIX (Live Board order mismatch): Persist the new order to the backend
  // via /coach/reorder-exercises. Without this, save-progress overlays
  // actual_sets onto the OLD positional slot, scrambling values on the Live
  // Board (e.g. hammer curl sets shown under the previous exercise).
  const handleMoveExercise = useCallback(async (fromIndex, direction) => {
    if (!isCoachMode) return;
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= workout.exercises.length) return;

    // Snapshot previous state so we can revert if the API call fails.
    const prevExercises = workout.exercises;
    const prevWorkoutData = workoutData;
    const prevCurrentIndex = currentExerciseIndex;

    const newExercises = [...workout.exercises];
    const [movedExercise] = newExercises.splice(fromIndex, 1);
    newExercises.splice(toIndex, 0, movedExercise);

    const newWorkoutData = [...workoutData];
    const [movedData] = newWorkoutData.splice(fromIndex, 1);
    newWorkoutData.splice(toIndex, 0, movedData);

    // Optimistic local update
    workout.exercises = newExercises;
    setWorkoutData(newWorkoutData);

    // Follow the exercise if it was the current one
    let nextCurrentIndex = currentExerciseIndex;
    if (currentExerciseIndex === fromIndex) {
      nextCurrentIndex = toIndex;
    } else if (fromIndex < currentExerciseIndex && toIndex >= currentExerciseIndex) {
      nextCurrentIndex = currentExerciseIndex - 1;
    } else if (fromIndex > currentExerciseIndex && toIndex <= currentExerciseIndex) {
      nextCurrentIndex = currentExerciseIndex + 1;
    }
    if (nextCurrentIndex !== currentExerciseIndex) {
      setCurrentExerciseIndex(nextCurrentIndex);
    }

    // Build the order payload BEFORE mutation:
    // exercise_order[i] = OLD index that now lives at NEW position i.
    const exerciseOrder = Array.from({ length: prevExercises.length }, (_, i) => i);
    const [movedIdx] = exerciseOrder.splice(fromIndex, 1);
    exerciseOrder.splice(toIndex, 0, movedIdx);

    try {
      await coachModeAxios(
        'put',
        `${API}/workouts/${workout.id}/coach/reorder-exercises`,
        { exercise_order: exerciseOrder }
      );
      console.log(`[REORDER] Persisted ${fromIndex} -> ${toIndex} (order=${JSON.stringify(exerciseOrder)})`);
    } catch (err) {
      console.error('[REORDER] Failed to persist new order, reverting:', err);
      // Revert local state so frontend stays consistent with the DB
      workout.exercises = prevExercises;
      setWorkoutData(prevWorkoutData);
      if (nextCurrentIndex !== prevCurrentIndex) {
        setCurrentExerciseIndex(prevCurrentIndex);
      }
      setCoachModeError(err.response?.data?.detail || 'No se pudo reordenar el ejercicio');
    }
  }, [isCoachMode, workout, workoutData, setWorkoutData, currentExerciseIndex, setCurrentExerciseIndex, coachModeAxios]);

  // Coach Mode: Update an exercise's target sets/reps/weight (e.g. after a
  // replace, so the new exercise doesn't inherit the previous one's numbers).
  const handleCoachUpdateTargets = useCallback(async (exerciseIndex, updates) => {
    if (!isCoachMode) return;
    const ex = workout.exercises[exerciseIndex];
    if (!ex) return;
    const applied = { ...ex };
    if (updates.target_sets != null) { applied.target_sets = updates.target_sets; applied.sets = updates.target_sets; }
    if (updates.target_reps != null) { applied.target_reps = updates.target_reps; applied.reps = updates.target_reps; }
    if (updates.target_weight != null) { applied.target_weight = updates.target_weight; applied.weight = updates.target_weight; }
    // Optimistic local update (keep logged actual_sets intact)
    workout.exercises[exerciseIndex] = applied;
    setWorkoutData((prev) => {
      const next = [...prev];
      if (next[exerciseIndex]) {
        next[exerciseIndex] = { ...next[exerciseIndex], ...applied, actual_sets: next[exerciseIndex].actual_sets };
      }
      return next;
    });
    try {
      await coachModeAxios('put', `${API}/workouts/${workout.id}/coach/update-targets/${exerciseIndex}`, updates);
    } catch (err) {
      console.error('[CoachMode] Error updating targets:', err);
      setCoachModeError('No se pudo actualizar el objetivo');
    }
  }, [isCoachMode, workout, setWorkoutData, coachModeAxios]);

  // Coach Mode: Apply to Template
  const handleCoachApplyToTemplate = useCallback(async () => {
    if (!isCoachMode) return;

    if (!window.confirm('¿Aplicar los cambios de este entrenamiento a la plantilla original?')) {
      return;
    }

    try {
      setCoachModeError(null);
      const response = await coachModeAxios('post', `${API}/workouts/${workout.id}/coach/apply-to-template`);
      alert(`✅ Plantilla "${response.data.template_name}" actualizada correctamente`);
      console.log('[CoachMode] Applied to template:', response.data);
    } catch (err) {
      console.error('[CoachMode] Error applying to template:', err);
      const errorMsg = err.response?.data?.detail || 'Error al actualizar la plantilla';
      setCoachModeError(errorMsg);
      alert(`❌ ${errorMsg}`);
    }
  }, [isCoachMode, workout.id, coachModeAxios]);

  return {
    coachModeError,
    setCoachModeError,
    coachModeAxios,
    openExercisePicker,
    handleCloseExercisePicker,
    verifyCoachPin,
    activateCoachMode,
    handleCoachAddExercise,
    handleCoachReplaceExercise,
    handleCoachRemoveExercise,
    handleMoveExercise,
    handleCoachUpdateTargets,
    handleCoachApplyToTemplate,
  };
};
